import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Store, 
  Loader2,
  ExternalLink,
  DollarSign,
  ShoppingCart
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DistributorResellers({ distributor }) {
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    if (distributor) {
      fetchResellers();
    }
  }, [distributor]);

  const fetchResellers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/distributor/resellers`, getAuthHeaders());
      setResellers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch resellers:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  const totalOrders = resellers.reduce((sum, r) => sum + (r.order_count || 0), 0);
  const totalCommission = resellers.reduce((sum, r) => sum + (r.total_commission || 0), 0);

  return (
    <div className="space-y-6" data-testid="distributor-resellers">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          My Resellers
        </h1>
        <p className="text-slate-500">Resellers assigned to you</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Store className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{resellers.length}</p>
              <p className="text-sm text-slate-500">Total Resellers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalOrders}</p>
              <p className="text-sm text-slate-500">Total Orders</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">${totalCommission.toFixed(2)}</p>
              <p className="text-sm text-slate-500">Total Commission</p>
            </div>
          </div>
        </div>
      </div>

      {/* Resellers Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {resellers.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No resellers assigned yet</p>
            <p className="text-sm text-slate-400">Contact admin to get resellers assigned to you</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Store</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-center">Orders</TableHead>
                <TableHead className="text-right">Commission Earned</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resellers.map((reseller) => (
                <TableRow key={reseller.store_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {reseller.logo_url ? (
                        <img 
                          src={reseller.logo_url} 
                          alt={reseller.business_name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Store className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{reseller.business_name}</p>
                        <p className="text-xs text-slate-500">{reseller.subdomain}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{reseller.contact_email}</p>
                    {reseller.contact_phone && (
                      <p className="text-xs text-slate-500">{reseller.contact_phone}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-center font-medium">
                    {reseller.order_count || 0}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-medium text-green-600">
                      ${(reseller.total_commission || 0).toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={reseller.is_active ? "default" : "secondary"}>
                      {reseller.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/store/${reseller.subdomain}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
